import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react-router';
import './App.css';

import MainLayout from './components/layout/MainLayout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ResumeProvider } from './context/ResumeContext';

// --- Page Components ---
import HomePage from '@/pages/HomePage';
import GenericPage from './pages/GenericPage';
import ContactPage from './pages/company/ContactPage';
import CareersPage from './pages/company/CareersPage';
import CareerSearchPage from './pages/company/careers/CareerSearchPage';
import JobDescriptionPage from './pages/company/careers/JobDescriptionPage';
import ApplyPage from './pages/company/careers/ApplyPage';
import ResumeDashboardPage from './pages/resume/ResumeDashboardPage';
import EditorPage from './pages/resume/EditorPage';
import FinalResumePage from './pages/resume/FinalResumePage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import SettingsLayout from './components/layout/SettingsLayout';
import AdminLayout from './components/layout/AdminLayout';
import LegacySectionRedirect from './components/resume/editor/LegacySectionRedirect';
import LoginPage from './pages/auth/LoginPage';
import AdminRoute from './components/auth/AdminRoute';
import AdminPage from './pages/admin/AdminPage';
import NotFoundPage from './pages/NotFoundPage';
import UserStatusPage from './pages/user/UserStatusPage';
import ChangePasswordPage from './pages/user/ChangePasswordPage';
import ProfilePage from './pages/user/ProfilePage';
import PricingPage from './pages/billing/PricingPage';
import CheckoutSuccessPage from './pages/billing/CheckoutSuccessPage';
import CheckoutCancelPage from './pages/billing/CheckoutCancelPage';
import CheckoutPage from './pages/billing/CheckoutPage';
import BillingSettingsPage from './pages/billing/BillingSettingsPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ContentManagerPage from './pages/admin/ContentManagerPage';
import CouponManagerPage from './pages/admin/CouponManagerPage';
import CancellationFeedbackPage from './pages/admin/CancellationFeedbackPage';
import ArticlePage from './pages/ArticlePage';

// --- JobBoard: feature-flagged ---
import JobBoardLegacy from './pages/job-board/JobBoard';      // .js, the old one
import JobBoardNew from './pages/job-board/JobBoard.tsx';     // .tsx, the bruh one
const USE_LEGACY_JOBBOARD = process.env.REACT_APP_LEGACY_JOBBOARD === '1';
const JobBoard = USE_LEGACY_JOBBOARD ? JobBoardLegacy : JobBoardNew;

// --- Shared QueryClient for the whole app ---
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// AuthProvider must wrap anything that calls useAuth, including App's body.
// Previously useAuth was called above the provider, returning undefined.
function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/" element={<MainLayout />}>
        <Route
          path="admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<AdminPage />} />
          <Route path="content-manager" element={<ContentManagerPage />} />
          <Route path="coupons" element={<CouponManagerPage />} />
          <Route path="cancellation-feedback" element={<CancellationFeedbackPage />} />
        </Route>
        <Route
          path="settings"
          element={
            <ProtectedRoute>
              <SettingsLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/settings/profile" replace />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="change-password" element={<ChangePasswordPage />} />
          <Route path="billing" element={<BillingSettingsPage />} />
        </Route>
        <Route
          path="upgrade"
          element={
            <ProtectedRoute>
              <PricingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="upgrade/success"
          element={
            <ProtectedRoute>
              <CheckoutSuccessPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="upgrade/cancel"
          element={
            <ProtectedRoute>
              <CheckoutCancelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="checkout"
          element={
            <ProtectedRoute>
              <CheckoutPage />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
        {/* --- Main Site Routes --- */}
        <Route index element={<HomePage />} />

        <Route path="company" element={<Navigate to="/company/contact" replace />} />
        <Route path="company/contact" element={<ContactPage />} />
        <Route path="company/careers" element={<CareersPage />} />
        <Route path="company/careers/search" element={<CareerSearchPage />} />
        <Route path="company/careers/jobs/:jobId" element={<JobDescriptionPage />} />
        <Route path="company/careers/jobs/:jobId/apply" element={<ApplyPage />} />

        <Route path='job_board' element={<JobBoard />} />

        <Route path="login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/news/:id" element={<ArticlePage />} />
        {/* --- Resume Routes --- */}
        <Route path="resume" element={<ResumeDashboardPage />} />
        <Route path="resume/:resumeId/final" element={<FinalResumePage />} />
        <Route
          path="resume/:resumeId"
          element={
            <ProtectedRoute>
              <EditorPage />
            </ProtectedRoute>
          }
        />
        <Route path="resume/:resumeId/:section" element={<LegacySectionRedirect />} />

        <Route
          path="my-status"
          element={
            <ProtectedRoute>
              {user?.role === 'client' ? (
                <UserStatusPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )}
            </ProtectedRoute>
          }
        />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <NuqsAdapter>
        <AuthProvider>
          <ResumeProvider>
            <AppRoutes />
          </ResumeProvider>
        </AuthProvider>
      </NuqsAdapter>
    </QueryClientProvider>
  );
}